/**
 * Normalise un décimal saisi en français (virgule) vers une string API (point).
 * Ex. "0,046789" → "0.046789" ; "50,00" → "50"
 */
export function normalizeDecimal(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number') {
        if (Number.isNaN(value)) return null;
        return String(value);
    }

    const trimmed = String(value).trim();
    if (!trimmed) return null;

    const normalized = trimmed.replace(/\s/g, '').replace(',', '.');
    const num = Number(normalized);
    if (Number.isNaN(num)) return null;
    return String(num);
}

/**
 * Convertit une date HTML (YYYY-MM-DD) vers le format attendu par SubCoverageCreate (DD/MM/YYYY).
 */
export function toApiDate(value) {
    if (!value || typeof value !== 'string') return value ?? null;

    // Déjà au format DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;

    // Format HTML date YYYY-MM-DD
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return `${day}/${month}/${year}`;
    }

    return value;
}

const emptyToNull = (value) => {
    if (value === '' || value === undefined) return null;
    return value;
};

const toOptionalInt = (value) => {
    if (value === '' || value == null) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Mappe les valeurs du formulaire vers le payload SubCoverageCreate exact.
 * Ne pas inclure partner_id / reference_id (hors body).
 */
export function formToSubCoveragePayload(formValues) {
    const payload = {
        trigger_type: String(formValues.trigger_type || '').toLowerCase(),
        data_source: String(formValues.data_source || '').toLowerCase(),
        unit: String(formValues.unit || '').toLowerCase(),
        trigger_low: normalizeDecimal(formValues.trigger_low),
        trigger_sup: normalizeDecimal(formValues.trigger_sup),
        timing_hour: toOptionalInt(formValues.timing_hour),
        timing_day: Number(formValues.timing_day),
        observation_timing_hour: Number(formValues.observation_timing_hour),
        observation_timing_day: String(formValues.observation_timing_day ?? '').trim(),
        siv_ratio_maintained: normalizeDecimal(formValues.siv_ratio_maintained),
        siv_ratio_cancelled: normalizeDecimal(formValues.siv_ratio_cancelled),
        prcp_trigger_hour_inf: normalizeDecimal(formValues.prcp_trigger_hour_inf),
        trigger_hour_sup: normalizeDecimal(formValues.trigger_hour_sup),
        prcp_trigger_number_hours: toOptionalInt(formValues.prcp_trigger_number_hours),
        orakle_price_ratio_excl_tsca: normalizeDecimal(formValues.orakle_price_ratio_excl_tsca),
        sub_coverage_start_date: toApiDate(formValues.sub_coverage_start_date),
        sub_coverage_end_date: toApiDate(formValues.sub_coverage_end_date),
        city: formValues.city ? String(formValues.city).trim() : '',
        country: formValues.country ? String(formValues.country).trim() : '',
        activity_address: formValues.activity_address
            ? String(formValues.activity_address).trim().toLowerCase()
            : '',
        zip_code: emptyToNull(
            formValues.zip_code != null && String(formValues.zip_code).trim() !== ''
                ? String(formValues.zip_code).trim()
                : null
        ),
        geo_longitude: normalizeDecimal(formValues.geo_longitude),
        geo_latitude: normalizeDecimal(formValues.geo_latitude),
    };

    return payload;
}

/**
 * Extrait les erreurs champ à partir d'une réponse FastAPI/Pydantic.
 */
export function extractFieldErrorsFromApi(error) {
    const detail = error?.response?.data?.detail;
    if (!Array.isArray(detail)) return {};

    const fieldErrors = {};
    detail.forEach((item) => {
        const loc = Array.isArray(item?.loc) ? item.loc : [];
        const field = loc.filter((part) => part !== 'body' && typeof part === 'string').pop();
        if (!field) return;
        const message = item.msg || item.message || 'Valeur invalide';
        fieldErrors[field] = message;
    });
    return fieldErrors;
}

/**
 * Message d'erreur API pour la création de sous-couverture.
 * Affiche le `detail` métier tel quel lorsqu'il s'agit d'une chaîne.
 */
export function getSubCoverageApiErrorMessage(
    error,
    fallback = "La sous-couverture n'a pas pu être créée. Vérifiez les informations saisies."
) {
    const detail = error?.response?.data?.detail;

    if (typeof detail === 'string' && detail.trim()) {
        return detail.trim();
    }

    if (Array.isArray(detail) && detail.length > 0) {
        const messages = detail
            .map((item) => item?.msg || item?.message || (typeof item === 'string' ? item : null))
            .filter(Boolean);
        if (messages.length > 0) return messages.join(' ');
    }

    if (typeof error?.response?.data?.message === 'string' && error.response.data.message.trim()) {
        return error.response.data.message.trim();
    }

    return fallback;
}
